/*
 * -----------------------------------------------------------------------
 *  <copyright file="dashboard-panel-list/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useCallback, useContext, useEffect, useState } from 'react';
import LazyLoad from 'react-lazy-load';
import { LocalStateContext } from 'contexts/local-state-context';
import { useTranslation, useWindowSize } from 'hooks';
import { PanelFields, PanelForObservationFields, DashboardFields } from 'queries/generated/fragments.generated';
import { DashboardType } from 'types/graphql-global-types';
import PanelFrame from 'views/components/core/panel-frame';
import './styles.scss';

interface DashboardPanelListInterface {
    dashboards: DashboardFields[] | null;
    type: DashboardType;
    allFullPanels: PanelForObservationFields[];
    selectedDate: string;
    onOpenCompanionView: (item: PanelFields) => void;
    onOpenAddPanelDialog: (panel: PanelFields) => void;
    canEdit: boolean;
    sitesStatusesToExclude: string[];
    siteId: string;
}

interface EmptyItem {
    empty: boolean;
}

interface DashboardPanel extends PanelFields, EmptyItem {
    id: string;
    studyId: string;
    name: string;
}

type PanelForObservationFieldsWithData = PanelForObservationFields & { panelData?: unknown };

type PanelDataWithPoints = { points?: { siteId?: string }[] } | undefined;

const DashboardPanelList = ({
    dashboards,
    type,
    allFullPanels,
    selectedDate,
    onOpenCompanionView,
    onOpenAddPanelDialog,
    canEdit,
    sitesStatusesToExclude,
    siteId,
}: DashboardPanelListInterface) => {
    const { t } = useTranslation();
    const { state } = useContext(LocalStateContext);
    const [filteredByDate, setFilteredByDate] = useState<DashboardPanel[] | null>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [emptyItems, setEmptyItems] = useState<EmptyItem[]>([]);
    const size = useWindowSize();

    const panelContainerRef = useCallback(
        (node: HTMLDivElement) => {
            if (node !== null) setContainerWidth(node.clientWidth);
        },
        [size],
    );

    useEffect(() => {
        if (dashboards?.length) {
            const dashboard = dashboards?.find((board) => board?.type?.toLowerCase() === type.toLowerCase());
            const panelsFromDash = dashboard?.panels?.sort((a, b) => (a?.index ?? 0) - (b?.index ?? 0));
            let panels =
                panelsFromDash?.map((panel) => {
                    const fullPanels = allFullPanels?.find(
                        (fullPanel) => fullPanel.id === panel?.panelId && fullPanel.active,
                    );
                    if (panel && fullPanels) {
                        const result: PanelForObservationFieldsWithData = { ...panel, ...fullPanels };
                        const panelData = state?.scoringData?.panelData.find(
                            (fullPanel) => (fullPanel as { panelId?: string })?.panelId === panel.panelId,
                        ) as PanelDataWithPoints;
                        if (panelData) {
                            panelData.points = panelData.points?.length
                                ? panelData.points.filter((item) => {
                                      if (item.siteId) {
                                          const site = state?.studyData?.sites.find(
                                              (element) =>
                                                  item.siteId ===
                                                  (element as { Fields: Record<string, string> })?.Fields['Site.id'],
                                          );
                                          if (site)
                                              return (
                                                  sitesStatusesToExclude.indexOf(
                                                      (site as { Fields: Record<string, string> }).Fields[
                                                          'Site.status'
                                                      ],
                                                  ) < 0
                                              );
                                      } else {
                                          return true;
                                      }
                                      return false;
                                  })
                                : panelData.points;
                            result.panelData = { panelData: [{ ...panelData }] };
                        }

                        return result;
                    }

                    return null;
                }) || [];

            panels = panels?.filter(Boolean);
            setFilteredByDate(panels as DashboardPanel[]);
        }
    }, [dashboards, state?.scoringData, state?.studyData, allFullPanels, type, selectedDate, sitesStatusesToExclude]);

    useEffect(() => {
        if (size.width > 1050 && containerWidth && filteredByDate?.length) {
            const items = filteredByDate.length;
            const itemsInRow = Math.floor(containerWidth / 500);
            const itemsToResize = items % itemsInRow;
            if (itemsToResize) {
                const newEmptyItems = [];
                const itemsToPush = itemsInRow - itemsToResize;
                for (let i = 0; i < itemsToPush; i++) {
                    newEmptyItems.push({ empty: true });
                }
                setEmptyItems(newEmptyItems);
            }
        }
    }, [panelContainerRef, filteredByDate, size]);

    const menuItems = useCallback(
        (panel: PanelFields) => [
            {
                label: t('cm:companionView'),
                icon: ['far', 'eye'],
                onClick: () => {
                    onOpenCompanionView(panel);
                },
                enabled: true,
            },
        ],
        [],
    );

    return (
        <div ref={panelContainerRef} className="panel-container">
            {state?.scoringData && state.scoringData.panelData?.length && filteredByDate?.length ? (
                <>
                    {filteredByDate?.map((panel) => (
                        <LazyLoad key={panel.id} className="panel-lazyload-wrapper">
                            <div id={panel.id} className="panel-item">
                                {panel.empty ? null : (
                                    <PanelFrame
                                        customKey={panel.id}
                                        title={panel.graph?.plotTitle || panel.name}
                                        siteId={type === DashboardType.SITE ? siteId : undefined}
                                        panel={panel}
                                        canEdit={canEdit}
                                        fullscreen
                                        menuItems={menuItems(panel)}
                                        onOpenAddPanelDialog={onOpenAddPanelDialog}
                                    />
                                )}
                            </div>
                        </LazyLoad>
                    ))}
                    {emptyItems.map(() => (
                        <div className="panel-item" />
                    ))}
                </>
            ) : null}
        </div>
    );
};

export default DashboardPanelList;
